import { useMutation, UseMutationOptions, useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import axiosAuth from "../axiosAuth";
import { FINANCIAL_TEMPLATES } from "./apiRoutes";

/** Financial Statement Versions (financial_template): list, structure, save draft, publish, new version, delete. */

export const useGetFinancialTemplates = ({ communityId, queryParams }: { communityId?: string; queryParams?: Omit<UseQueryOptions, "queryKey"> }): UseQueryResult<any> =>
  useQuery({
    queryKey: ["financial-templates", communityId],
    queryFn: () => axiosAuth.get(`${FINANCIAL_TEMPLATES}/community/${communityId}`),
    select: (data: any) => data.data?.data ?? [],
    enabled: !!communityId,
    refetchOnWindowFocus: false,
    ...queryParams,
  });

export const useGetFinancialTemplateStructure = ({ financialTemplateId, queryParams }: { financialTemplateId?: string; queryParams?: Omit<UseQueryOptions, "queryKey"> }): UseQueryResult<any> =>
  useQuery({
    queryKey: ["financial-template-structure", financialTemplateId],
    queryFn: () => axiosAuth.get(`${FINANCIAL_TEMPLATES}/${financialTemplateId}/structure`),
    select: (data: any) => data.data?.data,
    enabled: !!financialTemplateId,
    refetchOnWindowFocus: false,
    ...queryParams,
  });

type Opts<V> = UseMutationOptions<any, Error, V, unknown>;

export const useCreateFinancialTemplateMutation = (opts?: Opts<{ communityId: number; title: string; description?: string | null }>) =>
  useMutation({ mutationFn: async (data) => await axiosAuth.post(FINANCIAL_TEMPLATES, data), ...opts });

export const useSaveFinancialTemplateStructureMutation = (opts?: Opts<{ financialTemplateId: string; data: any }>) =>
  useMutation({
    mutationFn: async ({ financialTemplateId, data }) => await axiosAuth.put(`${FINANCIAL_TEMPLATES}/${financialTemplateId}/structure`, data),
    ...opts,
  });

export const usePublishFinancialTemplateMutation = (opts?: Opts<{ financialTemplateId: string }>) =>
  useMutation({
    mutationFn: async ({ financialTemplateId }) => await axiosAuth.post(`${FINANCIAL_TEMPLATES}/${financialTemplateId}/publish`),
    ...opts,
  });

/** New Version: copies the structure (same order) into the next version as a DRAFT. */
export const useCopyFinancialTemplateMutation = (opts?: Opts<{ financialTemplateId: string }>) =>
  useMutation({
    mutationFn: async ({ financialTemplateId }) => await axiosAuth.post(`${FINANCIAL_TEMPLATES}/${financialTemplateId}/copy`),
    ...opts,
  });

export const useDeleteFinancialTemplateMutation = (opts?: Opts<{ financialTemplateId: string }>) =>
  useMutation({
    mutationFn: async ({ financialTemplateId }) => await axiosAuth.delete(`${FINANCIAL_TEMPLATES}/${financialTemplateId}`),
    ...opts,
  });
